import React from "react";
import {Button, Form, Spinner} from "react-bootstrap";
import DatePicker from "react-date-picker";
import {ReactComponent as CalendarIcon} from '../../assets/icons/calendar.svg';
import {ReactComponent as ClearIcon} from '../../assets/icons/close.svg';
import {
    GHG_CONSTANTS,
    GHG_EMISSION_COLUMNS,
    GhgEditableField,
    GhgEmissionPeriod,
    GhgEmissionPeriodForm,
    isValidYearMonth,
} from "./models";

interface GhgEmissionPeriodEditableRowProps {
    form: GhgEmissionPeriodForm;
    validated: boolean;
    saving: boolean;
    period?: GhgEmissionPeriod;
    onChange: (change: Partial<GhgEmissionPeriodForm>) => void;
    onSave: () => void;
    onCancel: () => void;
}

function yearMonthToDate(value: string): Date | null {
  if (!isValidYearMonth(value)) return null;
  const [year, month] = value.split("-").map(Number);
  return new Date(year, month - 1, 1);
}

function dateToYearMonth(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${date.getFullYear()}-${month}`;
}

export function GhgEmissionPeriodEditableRow(props: GhgEmissionPeriodEditableRowProps) {
    const { form, validated, saving } = props;
    const isPeriodOrderInvalid = isValidYearMonth(form.periodStart) && isValidYearMonth(form.periodEnd) && form.periodStart > form.periodEnd;

    return (
        <tr className="ghg-emission-periods__editable-row">
            <td>
                <DatePicker
                    className={validated && (!isValidYearMonth(form.periodStart) || isPeriodOrderInvalid) ? 'invalid' : ''}
                    value={yearMonthToDate(form.periodStart)}
                    locale='et'
                    maxDetail='year'
                    format='MM.yyyy'
                    clearIcon={<ClearIcon/>}
                    calendarIcon={<CalendarIcon/>}
                    disabled={saving}
                    onChange={(event: any) => props.onChange({
                        periodStart: event ? dateToYearMonth(new Date(event)) : ""
                    })}
                />
            </td>
            <td>
                <DatePicker
                    className={validated && (!isValidYearMonth(form.periodEnd) || isPeriodOrderInvalid) ? 'invalid' : ''}
                    value={yearMonthToDate(form.periodEnd)}
                    locale='et'
                    maxDetail='year'
                    format='MM.yyyy'
                    clearIcon={<ClearIcon/>}
                    calendarIcon={<CalendarIcon/>}
                    disabled={saving}
                    onChange={(event: any) => props.onChange({
                        periodEnd: event ? dateToYearMonth(new Date(event)) : ""
                    })}
                />
            </td>
            {
                GHG_EMISSION_COLUMNS.map(({key, editable}) => {
                    if (!editable) {
                        const constant = props.period ? props.period[key] : GHG_CONSTANTS[key];
                        return (
                            <td key={key} className="text-nowrap">
                                {constant !== undefined ? String(constant) : ""}
                            </td>
                        );
                    }

                    const field = key as GhgEditableField;
                    const value = form[field];

                    return (
                        <td key={key}>
                            <Form.Control
                                type="number"
                                min={0}
                                step="any"
                                value={value}
                                isInvalid={validated && (value.trim() === "" || Number(value) < 0 || isNaN(Number(value)))}
                                disabled={saving}
                                onWheel={(e: any) => e.target.blur()}
                                onChange={(e: any) => props.onChange({[field]: e.target.value})}
                            />
                        </td>
                    );
                })
            }
            <td colSpan={2}>
                <div className="row__actions">
                    <Button
                        size="sm"
                        variant="primary"
                        className="d-flex align-items-center justify-content-center text-nowrap"
                        disabled={saving}
                        onClick={props.onSave}
                    >
                        {
                            saving ? (
                                <>
                                    <span className="px-1">Salvestan…</span>
                                    <Spinner size="sm" animation="border"/>
                                </>
                            ) : <span className="px-1">Salvesta</span>
                        }
                    </Button>
                    <Button
                        size="sm"
                        variant="info"
                        className="d-flex align-items-center justify-content-center text-nowrap"
                        disabled={saving}
                        onClick={props.onCancel}
                    >
                        <span className="px-1">Loobu</span>
                    </Button>
                </div>
            </td>
        </tr>
    );
}
